import { inject } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { ApolloClientOptions, ApolloLink, InMemoryCache, NormalizedCacheObject, DefaultOptions } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { HttpBatchLink, HttpLink } from 'apollo-angular/http';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';

import { environment } from '@env/environment';
import { CredentialsService } from '@app/core';
import { prefixUrl } from '@app/core/http/api-prefix.interceptor';

const DEFAULT_OPTIONS: DefaultOptions = { query: { fetchPolicy: 'network-only' } };

function createAuthLink(options: { isUpload?: boolean } = {}): ApolloLink {
  const credentialsService = inject(CredentialsService);
  return setContext((operation, { headers }) => {
    const authHeaders = {
      ...headers,
      ...credentialsService.authorizationHeader
    };

    return {
      headers: options.isUpload ? authHeaders : new HttpHeaders(authHeaders)
    };
  });
}

function createOptionalAuthLink(): ApolloLink {
  const credentialsService = inject(CredentialsService);
  return setContext((operation, { headers }) => {
    const authHeader = credentialsService.authorizationHeader;
    if (!authHeader) return { headers };

    return {
      headers: new HttpHeaders({
        ...headers,
        ...authHeader
      })
    };
  });
}

function createErrorLink() {
  return onError(({ graphQLErrors, networkError }) => {
    if (!graphQLErrors && networkError && !networkError.message) {
      const errorMessage = ((networkError as any).errors || [])[0]?.message;
      networkError.message = errorMessage || networkError.message;
    }
  });
}

function createMemoryCache() {
  return new InMemoryCache({
    typePolicies: {
      Member: {
        // Define uma chave única para cada objeto do tipo Member, garantindo
        // que seja usado 'id' ou 'user.id' para criar uma key exclusiva.
        keyFields: (object: any) => {
          const id = object.id || object.user?.id;
          return id ? `Member:${id}` : null;
        },
        // A função merge substitui o objeto existente pelo incoming,
        // evitando merges indesejados.
        merge(existing: any, incoming: any) {
          return incoming;
        }
      },
      Whitelabel: {
        keyFields: (object: any) => {
          const id = object.domain || location.href.split('?')[0];
          return id ? `Whitelabel:${id}` : null;
        },
        merge(existing: any, incoming: any) {
          return incoming;
        }
      }
    }
  });
}

export function createApolloClients(): { [key: string]: ApolloClientOptions<NormalizedCacheObject> } {
  const httpBatchLink = inject(HttpBatchLink);
  const httpLink = inject(HttpLink);

  /* @ts-ignore */
  globalThis.__DEV__ = false; // Stops Apollo from annoyingly logging in devtools about downloading their extension

  return {
    default: {
      link: ApolloLink.from([
        createErrorLink(),
        createAuthLink(),
        httpBatchLink.create({
          uri: '/graphql/secret',
          withCredentials: false,
          batchMax: 60
        })
      ]),
      cache: createMemoryCache(),
      defaultOptions: DEFAULT_OPTIONS,
      connectToDevTools: !environment.production || environment.debug
    },
    mfa: {
      link: ApolloLink.from([
        createErrorLink(),
        createAuthLink(),
        httpBatchLink.create({
          uri: '/graphql/secret/mfa',
          withCredentials: false,
          batchMax: 60
        })
      ]),
      cache: createMemoryCache(),
      defaultOptions: DEFAULT_OPTIONS,
      connectToDevTools: !environment.production || environment.debug
    },
    auth: {
      link: ApolloLink.from([
        createErrorLink(),
        httpLink.create({
          uri: '/graphql/auth',
          withCredentials: false
        })
      ]),
      cache: createMemoryCache(),
      defaultOptions: DEFAULT_OPTIONS
    },
    biometric: {
      link: ApolloLink.from([
        createErrorLink(),
        createOptionalAuthLink(),
        httpLink.create({
          uri: '/graphql/auth',
          withCredentials: false
        })
      ]),
      cache: createMemoryCache(),
      defaultOptions: DEFAULT_OPTIONS
    },
    public: {
      link: ApolloLink.from([
        createErrorLink(),
        httpBatchLink.create({
          uri: '/graphql/public',
          withCredentials: false,
          batchMax: 60
        })
      ]),
      cache: createMemoryCache(),
      defaultOptions: DEFAULT_OPTIONS
    },
    upload: {
      link: ApolloLink.from([
        createErrorLink(),
        createAuthLink({ isUpload: true }),
        createUploadLink({
          uri: prefixUrl('/graphql/secret'),
          credentials: 'omit'
        })
      ]),
      cache: new InMemoryCache(),
      defaultOptions: DEFAULT_OPTIONS
    }
  };
}
